import React, {useEffect, useMemo, useState} from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import deLocale from '@fullcalendar/core/locales/de';

import {getCurrentUserId, getCurrentUserRole} from '../utils/authHelper';
import {getSickEntries} from '../services/SickDayService';
import {getHolidayEntries} from '../services/HolidayService';
import {getPublicHolidays} from '../services/PublicHolidayServices';
import '../styles/Calendar.css';

export default function AbsenceCalendar() {
    const [events, setEvents] = useState([]);

    const userId = getCurrentUserId();
    const role = getCurrentUserRole();

    useEffect(() => {
        void loadCalendarData();
    }, []);

    const loadCalendarData = async () => {
        const eventsList = [];

        try {
            const [holidays, sick_days, publicHolidays] = await Promise.all([
                getHolidayEntries({role, userId}),
                getSickEntries({role, userId}),
                getPublicHolidays()
            ]);

            // Holiday
            holidays.forEach(e => {
                const start = new Date(e.startDate);
                const end = new Date(e.endDate);

                for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
                    const dateStr = new Date(d).toISOString().split('T')[0];
                    eventsList.push({
                        title: role === 'admin' ? `Urlaub – ${e.displayName}` : 'Urlaub',
                        date: dateStr,
                        color: '#4caf50',
                        extendedProps: {
                            type: 'holiday',
                            raw: e,
                            displayName: e.displayName,
                            startDate: e.startDate,
                            endDate: e.endDate,
                            numberDays: e.numberDays
                        }
                    });
                }
            });

            // Sick days
            sick_days.forEach(e => {
                const start = new Date(e.startDate);
                const end = new Date(e.endDate);

                for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
                    const dateStr = new Date(d).toISOString().split('T')[0];
                    eventsList.push({
                        title: role === 'admin' ? `Krank – ${e.displayName}` : 'Krank',
                        date: dateStr,
                        color: '#f44336',
                        extendedProps: {
                            type: 'sick',
                            raw: e,
                            displayName: e.displayName,
                            startDate: e.startDate,
                            endDate: e.endDate,
                            numberDays: e.numberDays
                        }
                    });
                }
            });

            // Public Holidays
            publicHolidays.forEach(e => {
                eventsList.push({
                    title: `Feiertag – ${e.description}`,
                    date: e.date,
                    color: '#2196f3',
                    extendedProps: {
                        type: 'publicHoliday',
                        description: e.description
                    }
                });
            });

            setEvents(eventsList);
        } catch (err) {
            console.error('Fehler beim Laden der Kalenderdaten:', err);
        }
    };

    // Build a tooltip text per day from all events of that day
    const tooltipByDate = useMemo(() => {
        const map = new Map();
        for (const ev of events) {
            const date = ev.date;
            const type = ev.extendedProps?.type;

            let line = ev.title;

            // Optional: enrich the tooltip with details for holiday/sick
            if ((type === 'holiday' || type === 'sick') && ev.extendedProps) {
                const s = ev.extendedProps.startDate ? String(ev.extendedProps.startDate).slice(0, 10) : '';
                const e = ev.extendedProps.endDate ? String(ev.extendedProps.endDate).slice(0, 10) : '';
                const days = ev.extendedProps.numberDays != null ? ` (${ev.extendedProps.numberDays} Tage)` : '';
                // Show range only if available
                if (s && e) line = `${ev.title} — ${s} bis ${e}${days}`;
                else line = `${ev.title}${days}`;
            }

            const arr = map.get(date) || [];
            arr.push(line);
            map.set(date, arr);
        }

        // Convert to multi-line strings
        const result = new Map();
        for (const [date, lines] of map.entries()) {
            result.set(date, lines.join('\n'));
        }
        return result;
    }, [events]);

    return (
        <div className="calendar-wrapper">
            <h3>Kalenderübersicht</h3>

            <FullCalendar
                plugins={[dayGridPlugin]}
                initialView="dayGridMonth"
                locale={deLocale}
                events={events}
                height="auto"

                dayMaxEvents={1}

                dayCellDidMount={(info) => {
                    const dateStr = info.date.toISOString().slice(0, 10);
                    const tooltip = tooltipByDate.get(dateStr);
                    if (tooltip) {
                        info.el.title = tooltip; // native tooltip (supports \n line breaks)
                    } else {
                        info.el.removeAttribute('title');
                    }
                }}
            />
        </div>
    );
}